import React, { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { compact } from 'lodash';
import LoadingRing from 'ui/atoms/loading-ring';
import useApiCall from 'hooks/use-api-call';
import { InvestorsApi, WalletsApi } from 'api/apis';
import { Wallet } from 'api/models';
import useInvestorMe from 'hooks/use-investor-me';
import WizardContext from 'libraries/wizard/wizard-context';
import Translate from 'ui/atoms/translate';
import { WalletType } from 'ui/types/wallet';
import WizardHeader from 'libraries/wizard/wizard-header';
import Header from 'ui/atoms/header';
import InvestmentSelectDepot from './select-depot';
import CreateDepot from './create-depot';
import CreateCashlinkWallet from './create-cashlink-wallet';
import useOnlyOnce from 'hooks/use-only-once';
import CreateWallet from './create-wallet';
import { useServerConfigSelector } from 'src/core/config/hooks';
import { useCurrentUserSelector } from 'src/core/auth/hooks';

const SelectWalletStep: FunctionComponent<{}> = () => {
  const { finalize, loading: contextLoading } = useContext(WizardContext);

  const [wallets, setWallets] = useState<Wallet[] | null>();
  const [nonExistingWalletsTypesSelection, setNonExistingWalletsTypesSelection] = useState<WalletType[] | null>();

  const { error: apiError, loading: apiLoading, makeAuthenticatedApi, withApi } = useApiCall();

  const { investor, error: investorError } = useInvestorMe();
  const { currentUser } = useCurrentUserSelector();

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);
  const walletsApi: WalletsApi = useMemo(() => makeAuthenticatedApi(WalletsApi), [makeAuthenticatedApi]);

  const {
    config: { registerOnlyFlow },
  } = useServerConfigSelector();

  useOnlyOnce(() => {
    withApi(async () => {
      setWallets(await walletsApi.walletsList());
    });
  });

  const onFinalizeCreateWallet = () => {
    finalize();
  };

  const createWallet = useCallback(
    async (address: string) => {
      await withApi(async () => {
        if (nonExistingWalletsTypesSelection?.includes(WalletType.GENERIC)) {
          await walletsApi.walletsGenericWalletCreate({
            genericWalletCreationRequest: { address },
          });
        }
      });

      finalize();
    },
    [withApi, walletsApi, nonExistingWalletsTypesSelection],
  );

  const createWalletSelection = useCallback(async () => {
    await withApi(async () => {
      if (!currentUser?.investor) return;

      await investorsApi.investorsWalletSelectionCreate({
        id: currentUser.investor,
        investorWalletSelectionRequest: {
          walletType: WalletType.MOBILE,
        },
      });

      finalize();
    });
  }, [withApi, finalize, currentUser?.investor]);

  const onDepotSelected = useCallback(
    async (selectedWallets: WalletType[]) => {
      // non-existing mobile safekeeping wallet:
      if (selectedWallets.includes(WalletType.MOBILE)) {
        await createWalletSelection();
      }
      // other non-existing wallets:
      else {
        setNonExistingWalletsTypesSelection(selectedWallets);
      }
    },
    [onFinalizeCreateWallet, setNonExistingWalletsTypesSelection, createWalletSelection],
  );

  const prioritizedAllowedWalletTypes: WalletType[] = compact([
    registerOnlyFlow.canUseCashlinkWalletEthereum && WalletType.CASHLINKETHEREUM,
    registerOnlyFlow?.canUseCashlinkWalletStellar && WalletType.CASHLINKSTELLAR,
    registerOnlyFlow.canUseTanganyWallet && WalletType.TANGANY,
    registerOnlyFlow.canUseMobileWallet && WalletType.MOBILE,
    WalletType.GENERIC,
  ]);

  const title = <Translate name="investmentDepot.title" />;

  if (investorError) return null;

  if (!wallets || !investor) return <LoadingRing />;

  return (
    <>
      <WizardHeader />
      <Header size="large" spacing="xlarge">
        {title}
      </Header>
      {((registerOnlyFlow.canUseCashlinkWalletEthereum &&
        nonExistingWalletsTypesSelection?.includes(WalletType.CASHLINKETHEREUM)) ||
        (registerOnlyFlow.canUseCashlinkWalletStellar &&
          nonExistingWalletsTypesSelection?.includes(WalletType.CASHLINKSTELLAR))) && (
        <CreateCashlinkWallet
          onWalletCreated={onFinalizeCreateWallet}
          onCancel={() => setNonExistingWalletsTypesSelection(null)}
          loading={apiLoading}
          error={apiError}
          walletTypes={nonExistingWalletsTypesSelection}
        />
      )}
      {registerOnlyFlow.canUseTanganyWallet && nonExistingWalletsTypesSelection?.includes(WalletType.TANGANY) && (
        <CreateDepot
          onWalletCreated={onFinalizeCreateWallet}
          onCancel={() => setNonExistingWalletsTypesSelection(null)}
          loading={apiLoading}
          error={apiError}
        />
      )}
      {nonExistingWalletsTypesSelection?.includes(WalletType.GENERIC) && (
        <CreateWallet
          onSubmit={({ address }) => createWallet(address)}
          onCancel={() => setNonExistingWalletsTypesSelection(null)}
          type={WalletType.GENERIC}
          loading={apiLoading}
          error={apiError}
        />
      )}
      {!nonExistingWalletsTypesSelection?.length && (
        <InvestmentSelectDepot
          loading={apiLoading || contextLoading}
          error={apiError}
          onDepotSelected={onDepotSelected}
          prioritizedAllowedWalletTypes={prioritizedAllowedWalletTypes}
          tfaDevice={currentUser?.tfaDevice || undefined}
        />
      )}
    </>
  );
};

export default SelectWalletStep;
